/**
 * Mock security policy service — backed by the in-memory `mockSecurityPolicy`
 * seed.
 *
 * Updates persist for the lifetime of the page (the factory memoizes the
 * instance), so saving on the Security Settings screen survives in-session
 * reloads of its own data even in mock mode.
 */

import { mockSecurityPolicy } from '@/data/mockSecurityPolicy'
import { mockDelay } from '@/services/factory'
import type { SecurityPolicyService } from './securityPolicy.service'
import type {
  SecurityPolicy,
  UpdateSecurityPolicyInput,
} from '@/types/securityPolicy.types'

export function createMockSecurityPolicyService(): SecurityPolicyService {
  // Session-local copy seeded from the fixture (deep-copied so edits don't leak
  // into the shared module-level seed).
  const state: SecurityPolicy = structuredClone(mockSecurityPolicy)

  return {
    async get(): Promise<SecurityPolicy> {
      await mockDelay(300)
      return structuredClone(state)
    },

    async update(patch: UpdateSecurityPolicyInput): Promise<SecurityPolicy> {
      await mockDelay(400)
      if (patch.passwordPolicy !== undefined) state.passwordPolicy = patch.passwordPolicy
      if (patch.sessionPolicy !== undefined) state.sessionPolicy = patch.sessionPolicy
      if (patch.loginPolicy !== undefined) state.loginPolicy = patch.loginPolicy
      return structuredClone(state)
    },
  }
}
